/**
 * MetricCard — Large metric display with label and optional badge.
 *
 * Design law: docs/plans/2026-07-24-k3-black-orange-design-law.md §2
 * - DM Mono for value (font-mono), Outfit for label (font-sans)
 * - danger/blocked/error badges use the red BLOCKER tokens — distinct from
 *   the orange active/brand states (they previously shared one tint)
 * - success/done badges are the hueless VERIFIED treatment (no green)
 * - Clickable cards render as a real <button> so Enter/Space and the
 *   app-wide :focus-visible ring work natively.
 */

import React from 'react';
import { cn } from '../../lib/utils';
import { STATE_STYLES } from './StateChip';

interface MetricCardProps {
  label: string;
  value: React.ReactNode;
  subtitle?: string;
  badge?: {
    text: string;
    variant:
      | 'success' | 'warning' | 'danger' | 'info' | 'neutral'
      | 'active' | 'pending' | 'done' | 'attention' | 'blocked' | 'error';
  };
  accent?: boolean; // Orange left-accent for "needs action"
  className?: string;
  onClick?: () => void;
  testId?: string;
}

const BRAND_BADGE = 'bg-[var(--color-needs-human-tint)] text-[var(--color-needs-human-text)] border-[var(--color-needs-human-tint-bd)]';
const NEUTRAL_BADGE = 'bg-[var(--color-bg-elevated)] text-[var(--color-text-muted)] border-[var(--color-border)]';

const badgeVariants = {
  // Hueless verified — the absence of signal IS the signal.
  success: STATE_STYLES.verified,
  done: STATE_STYLES.verified,

  // Blocker red — legal consequence, never the same tint as "running".
  danger: STATE_STYLES.blocker,
  blocked: STATE_STYLES.blocker,
  error: STATE_STYLES.blocker,

  // Orange — act/review.
  active: BRAND_BADGE,
  pending: BRAND_BADGE,
  warning: BRAND_BADGE,
  attention: BRAND_BADGE,

  // Gray.
  info: NEUTRAL_BADGE,
  neutral: NEUTRAL_BADGE,
};

export function MetricCard({
  label,
  value,
  subtitle,
  badge,
  accent = false,
  className,
  onClick,
  testId,
}: MetricCardProps) {
  const interactive = Boolean(onClick);
  const Comp = (interactive ? 'button' : 'div') as 'button';

  return (
    <Comp
      type={interactive ? 'button' : undefined}
      data-testid={testId}
      className={cn(
        'rounded-[13px] border p-4',
        'bg-[var(--color-bg-surface)] border-[var(--color-border)]',
        accent
          ? 'border-l-[3px] border-l-[var(--color-brand)]'
          : 'border-l-[3px] border-l-[var(--color-status-queued)]',
        // Real <button>: neutralize UA styles so it lays out like the div did;
        // the app-wide :focus-visible ring (index.css) applies natively.
        interactive &&
          'block w-full appearance-none text-left cursor-pointer hover:bg-[var(--color-bg-hover)] transition-colors',
        className
      )}
      onClick={onClick}
      aria-label={typeof value === 'string' || typeof value === 'number' ? `${label}: ${value}` : label}
    >
      <div className="flex items-start justify-between gap-2">
        <p className="font-sans text-sm font-medium text-[var(--color-text-muted)] min-w-0 truncate">
          {label}
        </p>
        {badge && (
          <span
            className={cn(
              'inline-flex shrink-0 items-center whitespace-nowrap rounded-full border px-2.5 py-0.5 font-sans text-xs font-semibold',
              badgeVariants[badge.variant]
            )}
          >
            {badge.text}
          </span>
        )}
      </div>
      <p className="mt-1 font-mono text-2xl font-bold tabular-nums text-[var(--color-text-primary)] break-words">
        {value}
      </p>
      {subtitle && (
        <p className="mt-1 font-sans text-xs text-[var(--color-text-dim)]">{subtitle}</p>
      )}
    </Comp>
  );
}

export default MetricCard;
